import React, { useCallback, useMemo, useState, Suspense, lazy } from 'react';
import { Check, Star, AlertTriangle, ShieldCheck, ArrowRight, X } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Accordion } from '../components/ui/Accordion';
import { CountdownTimer } from '../components/CountdownTimer';
const SalesPopup = lazy(() => import('../components/SalesPopup').then(m => ({
  default: m.SalesPopup
})));
const IMAGES = {
  main: 'https://cdn.magicpatterns.com/uploads/4sozSwtga3ShjiJsWRa7Re/Untitled_design-removebg-preview_(1).png',
  bono1: 'https://cdn.magicpatterns.com/uploads/niNbwmfaz6frkNS3YgnATj/mocap_bono_1.png',
  bono2: 'https://cdn.magicpatterns.com/uploads/qPkY6DaztduWFXBhcYSuDM/mocap_bono_2.png',
  bono3: 'https://cdn.magicpatterns.com/uploads/bSLuWY4XC7CYVqrZRqxSbN/mocap_bono_3.png',
  bono4: 'https://cdn.magicpatterns.com/uploads/v8tmoymo72A91sPabtXqFd/mocap_bono_4.png'
};
const CHECKOUT_URLS: Record<string, string> = {
  basico: 'https://checkout.ars.descargatop.com/cart/47492343398658:1',
  completo: 'https://checkout.ars.descargatop.com/cart/47492397793538:1'
};
export function LandingPage() {
  const [selectedPack, setSelectedPack] = useState('completo');
  const scrollToPlans = useCallback(() => {
    document.getElementById('elegir-plan')?.scrollIntoView({
      behavior: 'smooth'
    });
  }, []);
  const handleBuyClick = useCallback(() => {
    window.location.href = CHECKOUT_URLS[selectedPack];
  }, [selectedPack]);
  return <div className="min-h-screen bg-white font-sans text-gray-900 pb-20" data-id="element-25">
      <Suspense fallback={null} data-id="element-26">
        <SalesPopup data-id="element-27" />
      </Suspense>

      {/* 1. Sticky Top Bar - Urgency Banner */}
      <CountdownTimer data-id="element-28" />

      {/* Main Container */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8" data-id="element-29">
        {/* 2. Hero Section */}
        <section className="py-8 md:py-12 text-center" data-id="element-30">
          <p className="text-[#cc0000] font-bold tracking-wider uppercase mb-4 text-sm md:text-base" data-id="element-31">
            🐶 MEGAPACK PASTELERÍA Y NUTRICIÓN CANINA
          </p>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 text-gray-900" data-id="element-32">
            Emprende • Alimenta Mejor •{' '}
            <span className="text-[#ff9900]" data-id="element-33">Genera Ingresos</span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto font-medium" data-id="element-34">
            Recetas reales. Nutrición responsable. Un negocio desde casa.
          </p>

          <div className="mb-10" data-id="element-35">
            <img src={IMAGES.main} alt="Megapack Pastelería Canina - 6 Tomos + 4 Bonos" className="w-full max-w-2xl mx-auto shadow-2xl rounded-lg" fetchPriority={window.innerWidth >= 768 ? 'high' : 'low'} loading={window.innerWidth >= 768 ? 'eager' : 'lazy'} decoding="async" width={672} height={504} data-id="element-36" />
          </div>

          <div className="bg-yellow-50 border-l-4 border-[#ff9900] p-4 mb-8 text-left max-w-2xl mx-auto" data-id="element-37">
            <p className="font-semibold text-gray-800" data-id="element-38">
              Aprendé paso a paso cómo preparar alimentos saludables para perros
              y convertirlo en una fuente de ingresos.
            </p>
          </div>

          <Button variant="primary" size="xl" onClick={scrollToPlans} className="w-full md:w-auto" aria-label="Ver planes disponibles" data-id="element-39">
            👉 SÍ, QUIERO ACCEDER AHORA
          </Button>

          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-600" data-id="element-40">
            <ShieldCheck className="w-4 h-4 text-green-500" aria-hidden="true" data-id="element-41" />
            <span data-id="element-42">Garantía de satisfacción de 7 días</span>
          </div>
        </section>

        {/* 3. Agitation Section - Pain Points */}
        <section className="py-12 border-t border-gray-100 cv-auto" data-id="element-43">
          <div className="prose prose-lg mx-auto text-gray-700 max-w-3xl" data-id="element-44">
            <p className="font-bold text-xl text-gray-900" data-id="element-45">
              Si amas a tu perro o quieres emprender desde casa, dejame hacerte
              una pregunta honesta…
            </p>

            <ul className="space-y-4 list-none pl-0 my-8" role="list" data-id="element-46">
              <li className="flex gap-3 items-start" data-id="element-47">
                <AlertTriangle className="w-6 h-6 text-[#cc0000] flex-shrink-0 mt-1" aria-hidden="true" data-id="element-48" />
                <span data-id="element-49">
                  ¿Te frustra no saber exactamente qué está comiendo tu mascota?
                </span>
              </li>
              <li className="flex gap-3 items-start" data-id="element-50">
                <AlertTriangle className="w-6 h-6 text-[#cc0000] flex-shrink-0 mt-1" aria-hidden="true" data-id="element-51" />
                <span data-id="element-52">
                  ¿Te gustaría generar ingresos extra pero no sabes por dónde
                  empezar?
                </span>
              </li>
              <li className="flex gap-3 items-start" data-id="element-53">
                <AlertTriangle className="w-6 h-6 text-[#cc0000] flex-shrink-0 mt-1" aria-hidden="true" data-id="element-54" />
                <span data-id="element-55">
                  ¿Sientes que podrías hacer algo más… si tuvieras una guía
                  clara y paso a paso?
                </span>
              </li>
            </ul>

            <p data-id="element-56">
              No porque no quieras. No porque no tengas talento. <br data-id="element-57" />
              <span className="font-bold" data-id="element-58">
                Sino porque nadie te enseñó cómo hacerlo correctamente.
              </span>
            </p>

            <p className="bg-gray-100 p-4 rounded-lg border-l-4 border-gray-400 italic my-6" data-id="element-59">
              Y mientras tanto… <br data-id="element-60" />
              Tu perro sigue comiendo productos industriales. <br data-id="element-61" />Y tú sigues
              postergando esa idea de emprender desde casa.
            </p>

            <h3 className="text-2xl font-bold text-[#cc0000] mt-12 mb-6 text-center" data-id="element-62">
              El problema que nadie dice en voz alta
            </h3>

            <p data-id="element-63">Si esto te pasa, no estás sola/o:</p>

            <div className="grid md:grid-cols-2 gap-4 my-6" data-id="element-64">
              <div className="flex items-center gap-2 text-gray-700" data-id="element-65">
                <X className="w-5 h-5 text-red-500 flex-shrink-0" aria-hidden="true" data-id="element-66" />
                <span data-id="element-67">Compras snacks sin saber realmente qué contienen</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700" data-id="element-68">
                <X className="w-5 h-5 text-red-500 flex-shrink-0" aria-hidden="true" data-id="element-69" />
                <span data-id="element-70">Quieres emprender pero no sabes qué vender</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700" data-id="element-71">
                <X className="w-5 h-5 text-red-500 flex-shrink-0" aria-hidden="true" data-id="element-72" />
                <span data-id="element-73">Te preocupa la salud de tu mascota</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700" data-id="element-74">
                <X className="w-5 h-5 text-red-500 flex-shrink-0" aria-hidden="true" data-id="element-75" />
                <span data-id="element-76">No sabes cómo calcular precios</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700" data-id="element-77">
                <X className="w-5 h-5 text-red-500 flex-shrink-0" aria-hidden="true" data-id="element-78" />
                <span data-id="element-79">Tienes miedo de hacerlo mal</span>
              </div>
            </div>

            <p className="text-center font-bold text-lg mt-8" data-id="element-80">
              Y aparece ese pensamiento incómodo: <br data-id="element-81" />
              <span className="text-2xl block mt-2 font-extrabold text-gray-900" data-id="element-82">
                "Si tuviera una guía clara… lo intentaría."
              </span>
            </p>
          </div>
        </section>

        {/* 4. Solution Section */}
        <section className="py-12 bg-green-50 -mx-4 sm:-mx-6 md:-mx-8 px-4 sm:px-6 md:px-8 cv-auto" data-id="element-83">
          <div className="max-w-4xl mx-auto text-center" data-id="element-84">
            <h2 className="text-3xl font-extrabold text-green-700 mb-8" data-id="element-85">
              ✅ La solución
            </h2>
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-86">
              🐶 MEGAPACK PASTELERÍA Y NUTRICIÓN CANINA
            </h3>

            <p className="text-lg text-gray-700 mb-8" data-id="element-87">
              Un sistema completo que te enseña:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto mb-10 text-left" data-id="element-88">
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-89">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" aria-hidden="true" data-id="element-90" />
                <span className="font-semibold text-gray-800" data-id="element-91">
                  Nutrición canina desde cero
                </span>
              </div>
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-92">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" aria-hidden="true" data-id="element-93" />
                <span className="font-semibold text-gray-800" data-id="element-94">
                  Recetas saludables paso a paso
                </span>
              </div>
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-95">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" aria-hidden="true" data-id="element-96" />
                <span className="font-semibold text-gray-800" data-id="element-97">
                  Adaptación para alergias e intolerancias
                </span>
              </div>
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-98">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" aria-hidden="true" data-id="element-99" />
                <span className="font-semibold text-gray-800" data-id="element-100">
                  Técnicas profesionales
                </span>
              </div>
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm md:col-span-2" data-id="element-101">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" aria-hidden="true" data-id="element-102" />
                <span className="font-semibold text-gray-800" data-id="element-103">
                  Cómo vender y generar ingresos
                </span>
              </div>
            </div>

            <div className="flex flex-col md:flex-row justify-center items-center gap-6 text-xl font-bold text-gray-800 mb-10" data-id="element-104">
              <div className="flex flex-col items-center" data-id="element-105">
                <span className="text-4xl mb-2" aria-hidden="true" data-id="element-106">
                  📖
                </span>
                <span data-id="element-107">Abrís el Megapack</span>
              </div>
              <ArrowRight className="hidden md:block text-gray-400" aria-hidden="true" data-id="element-108" />
              <div className="flex flex-col items-center" data-id="element-109">
                <span className="text-4xl mb-2" aria-hidden="true" data-id="element-110">
                  👩‍🍳
                </span>
                <span data-id="element-111">Preparás las recetas</span>
              </div>
              <ArrowRight className="hidden md:block text-gray-400" aria-hidden="true" data-id="element-112" />
              <div className="flex flex-col items-center" data-id="element-113">
                <span className="text-4xl mb-2" aria-hidden="true" data-id="element-114">
                  💰
                </span>
                <span data-id="element-115">Comenzás a vender</span>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-4 text-sm font-semibold text-gray-600 uppercase tracking-wide" data-id="element-116">
              <span data-id="element-117">Sin experiencia previa</span>
              <span aria-hidden="true" data-id="element-118">•</span>
              <span data-id="element-119">Sin cursos costosos</span>
              <span aria-hidden="true" data-id="element-120">•</span>
              <span data-id="element-121">Sin salir de casa</span>
            </div>
          </div>
        </section>

        {/* 5. Product Modules - 6 Tomos */}
        <section className="py-16 cv-auto" data-id="element-122">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center text-gray-900 mb-12" data-id="element-123">
            📚 ¿Qué incluye exactamente el Megapack?
          </h2>

          <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-100 mb-12" data-id="element-124">
            <h3 className="text-2xl font-bold text-blue-800 mb-6 text-center" data-id="element-125">
              📘 6 TOMOS PROFESIONALES
            </h3>
            <div className="space-y-6" data-id="element-126">
              <div className="flex gap-4 items-start" data-id="element-127">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold mt-1" data-id="element-128">
                  1
                </div>
                <div data-id="element-129">
                  <h4 className="font-bold text-lg text-gray-900" data-id="element-130">
                    Tomo 1 – Fundamentos de Nutrición y Salud Canina
                  </h4>
                  <p className="text-gray-600" data-id="element-131">
                    Aprende cómo funciona la alimentación canina y cómo crear
                    recetas equilibradas.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-132">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold mt-1" data-id="element-133">
                  2
                </div>
                <div data-id="element-134">
                  <h4 className="font-bold text-lg text-gray-900" data-id="element-135">
                    Tomo 2 – Panadería y Snacks Caninos
                  </h4>
                  <p className="text-gray-600" data-id="element-136">
                    Galletas, panecillos y productos horneados fáciles y
                    vendibles.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-137">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold mt-1" data-id="element-138">
                  3
                </div>
                <div data-id="element-139">
                  <h4 className="font-bold text-lg text-gray-900" data-id="element-140">
                    Tomo 3 – Snacks Funcionales y Naturales
                  </h4>
                  <p className="text-gray-600" data-id="element-141">
                    Recetas que aportan beneficios digestivos, energéticos y de
                    bienestar.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-142">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold mt-1" data-id="element-143">
                  4
                </div>
                <div data-id="element-144">
                  <h4 className="font-bold text-lg text-gray-900" data-id="element-145">
                    Tomo 4 – Pasteles y Celebraciones Caninas
                  </h4>
                  <p className="text-gray-600" data-id="element-146">
                    Tortas y productos premium para cumpleaños y eventos.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-147">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold mt-1" data-id="element-148">
                  5
                </div>
                <div data-id="element-149">
                  <h4 className="font-bold text-lg text-gray-900" data-id="element-150">
                    Tomo 5 – Helados y Recetas Refrescantes
                  </h4>
                  <p className="text-gray-600" data-id="element-151">
                    Opciones innovadoras para diferenciarte en el mercado.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-152">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold mt-1" data-id="element-153">
                  6
                </div>
                <div data-id="element-154">
                  <h4 className="font-bold text-lg text-gray-900" data-id="element-155">
                    Tomo 6 – Emprendimiento en Pastelería Canina
                  </h4>
                  <p className="text-gray-600" data-id="element-156">
                    Cómo calcular precios, presentar productos y generar
                    ingresos reales.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 6. Bonuses - 4 Bonus Products */}
        <section className="py-12 cv-auto" data-id="element-157">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center text-[#cc0000] mb-4" data-id="element-158">
            🎁 BONOS INCLUIDOS HOY
          </h2>
          <p className="text-center text-gray-600 mb-12 uppercase tracking-widest font-bold" data-id="element-159">
            (Sin costo adicional)
          </p>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-160">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow" data-id="element-161">
              <div className="bg-gray-50 p-4 flex justify-center" data-id="element-162">
                <img src={IMAGES.bono1} alt="Bono 1 - Nutrición y Pastelería Felina" className="h-64 object-contain" loading="lazy" decoding="async" data-id="element-163" />
              </div>
              <div className="p-6" data-id="element-164">
                <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded uppercase mb-2 inline-block" data-id="element-165">
                  Gratis Hoy
                </span>
                <h3 className="font-bold text-xl text-gray-900 mb-2" data-id="element-166">
                  Bono #1 – Nutrición y Pastelería Felina
                </h3>
                <p className="text-gray-600" data-id="element-167">
                  Amplía tu mercado aprendiendo también recetas para gatos.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow" data-id="element-168">
              <div className="bg-gray-50 p-4 flex justify-center" data-id="element-169">
                <img src={IMAGES.bono2} alt="Bono 2 - Dieta BARF Paso a Paso" className="h-64 object-contain" loading="lazy" decoding="async" data-id="element-170" />
              </div>
              <div className="p-6" data-id="element-171">
                <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded uppercase mb-2 inline-block" data-id="element-172">
                  Gratis Hoy
                </span>
                <h3 className="font-bold text-xl text-gray-900 mb-2" data-id="element-173">
                  Bono #2 – Dieta BARF Paso a Paso
                </h3>
                <p className="text-gray-600" data-id="element-174">
                  Aprende alimentación natural correctamente y sin riesgos.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow" data-id="element-175">
              <div className="bg-gray-50 p-4 flex justify-center" data-id="element-176">
                <img src={IMAGES.bono3} alt="Bono 3 - Guía para Prevenir Enfermedades en Casa" className="h-64 object-contain" loading="lazy" decoding="async" data-id="element-177" />
              </div>
              <div className="p-6" data-id="element-178">
                <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded uppercase mb-2 inline-block" data-id="element-179">
                  Gratis Hoy
                </span>
                <h3 className="font-bold text-xl text-gray-900 mb-2" data-id="element-180">
                  Bono #3 – Guía para Prevenir Enfermedades en Casa
                </h3>
                <p className="text-gray-600" data-id="element-181">
                  Cómo cuidar la salud de tu perro desde casa.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow" data-id="element-182">
              <div className="bg-gray-50 p-4 flex justify-center" data-id="element-183">
                <img src={IMAGES.bono4} alt="Bono 4 - Cachorro en Casa" className="h-64 object-contain" loading="lazy" decoding="async" data-id="element-184" />
              </div>
              <div className="p-6" data-id="element-185">
                <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded uppercase mb-2 inline-block" data-id="element-186">
                  Gratis Hoy
                </span>
                <h3 className="font-bold text-xl text-gray-900 mb-2" data-id="element-187">
                  Bono #4 – Cachorro en Casa
                </h3>
                <p className="text-gray-600" data-id="element-188">
                  Crianza, alimentación y entrenamiento desde el primer día.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12" data-id="element-189">
            <p className="text-2xl font-bold text-gray-800" data-id="element-190">
              👉 6 Tomos + 4 Bonos en un solo Megapack.
            </p>
          </div>
        </section>

        {/* 7. Before vs After Comparison */}
        <section className="py-12 border-t border-b border-gray-100 bg-gray-50 -mx-4 sm:-mx-6 md:-mx-8 px-4 sm:px-6 md:px-8 cv-auto" data-id="element-191">
          <div className="max-w-4xl mx-auto" data-id="element-192">
            <h2 className="text-3xl font-extrabold text-center mb-10" data-id="element-193">
              🔄 Antes vs Después
            </h2>

            <div className="grid md:grid-cols-2 gap-8" data-id="element-194">
              <div className="bg-white p-8 rounded-xl shadow-sm border-t-4 border-red-500" data-id="element-195">
                <h3 className="text-xl font-bold text-red-600 mb-6 flex items-center gap-2" data-id="element-196">
                  <X className="w-6 h-6" aria-hidden="true" data-id="element-197" /> Antes del
                  Megapack
                </h3>
                <ul className="space-y-4" role="list" data-id="element-198">
                  <li className="flex items-start gap-3 text-gray-600" data-id="element-199">
                    <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-200" />
                    <span data-id="element-201">No sabes qué ingredientes usar</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-600" data-id="element-202">
                    <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-203" />
                    <span data-id="element-204">Compras snacks industriales</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-600" data-id="element-205">
                    <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-206" />
                    <span data-id="element-207">No tienes ingresos extra</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-600" data-id="element-208">
                    <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-209" />
                    <span data-id="element-210">Tienes dudas sobre la nutrición</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-600" data-id="element-211">
                    <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-212" />
                    <span data-id="element-213">Postergas la idea de emprender</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-green-500 transform md:-translate-y-4" data-id="element-214">
                <h3 className="text-xl font-bold text-green-600 mb-6 flex items-center gap-2" data-id="element-215">
                  <Check className="w-6 h-6" aria-hidden="true" data-id="element-216" /> Después del
                  Megapack
                </h3>
                <ul className="space-y-4" role="list" data-id="element-217">
                  <li className="flex items-start gap-3 text-gray-800 font-medium" data-id="element-218">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-219" />
                    <span data-id="element-220">Preparas alimentos saludables</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-800 font-medium" data-id="element-221">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-222" />
                    <span data-id="element-223">Sabes exactamente qué estás usando</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-800 font-medium" data-id="element-224">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-225" />
                    <span data-id="element-226">Generas ingresos desde casa</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-800 font-medium" data-id="element-227">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-228" />
                    <span data-id="element-229">Te diferencias con productos premium</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-800 font-medium" data-id="element-230">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-231" />
                    <span data-id="element-232">Confianza para vender</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 8. Testimonials */}
        <section className="py-12 bg-gray-50 -mx-4 sm:-mx-6 md:-mx-8 px-4 sm:px-6 md:px-8 cv-auto" data-id="element-233">
          <div className="max-w-4xl mx-auto" data-id="element-234">
            <h2 className="text-3xl font-extrabold text-center mb-12" data-id="element-235">
              💬 Lo que dicen nuestras alumnas
            </h2>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-236">
              <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-237">
                <div className="flex text-yellow-400 mb-3" aria-label="5 estrellas" data-id="element-238">
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-239" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-240" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-241" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-242" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-243" />
                </div>
                <p className="text-gray-700 italic mb-4" data-id="element-244">
                  "Empecé preparando para mi perro… ahora vendo todas las
                  semanas."
                </p>
                <p className="font-bold text-gray-900 text-sm" data-id="element-245">
                  — Laura, emprendedora
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-246">
                <div className="flex text-yellow-400 mb-3" aria-label="5 estrellas" data-id="element-247">
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-248" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-249" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-250" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-251" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-252" />
                </div>
                <p className="text-gray-700 italic mb-4" data-id="element-253">
                  "No sabía nada de nutrición. Ahora preparo todo en casa con
                  seguridad."
                </p>
                <p className="font-bold text-gray-900 text-sm" data-id="element-254">
                  — Mariana, mamá perruna
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-255">
                <div className="flex text-yellow-400 mb-3" aria-label="5 estrellas" data-id="element-256">
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-257" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-258" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-259" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-260" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-261" />
                </div>
                <p className="text-gray-700 italic mb-4" data-id="element-262">
                  "En 2 semanas recuperé la inversión."
                </p>
                <p className="font-bold text-gray-900 text-sm" data-id="element-263">
                  — Carla, pastelería casera
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-264">
                <div className="flex text-yellow-400 mb-3" aria-label="5 estrellas" data-id="element-265">
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-266" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-267" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-268" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-269" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-270" />
                </div>
                <p className="text-gray-700 italic mb-4" data-id="element-271">
                  "No es solo un recetario, es un negocio."
                </p>
                <p className="font-bold text-gray-900 text-sm" data-id="element-272">— Andrea</p>
              </div>
            </div>
          </div>
        </section>

        {/* 9. Pricing - Plan Selector */}
        <section id="elegir-plan" className="py-16" data-id="element-273">
          <div className="bg-gray-900 text-white rounded-2xl p-8 md:p-12 shadow-2xl" data-id="element-274">
            <div className="max-w-2xl mx-auto" data-id="element-275">
              <h3 className="text-2xl md:text-3xl font-extrabold text-center text-yellow-400 mb-2" data-id="element-276">
                🛒 ELEGÍ TU PACK Y EMPEZÁ HOY
              </h3>
              <p className="text-center text-gray-400 mb-2" data-id="element-277">
                Descarga inmediata. Acceso de por vida.
              </p>
              <p className="text-center text-yellow-400 font-bold mb-8" data-id="element-278">
                👇 Elegí tu pack y dale a COMPRAR AHORA 👇
              </p>

              <div className="space-y-4" data-id="element-279">
                {/* Pack Básico */}
                <div className={`rounded-xl border-2 transition-all cursor-pointer ${selectedPack === 'basico' ? 'border-yellow-400 bg-gray-800' : 'border-gray-700 bg-gray-800/50 hover:border-gray-500'}`} onClick={() => setSelectedPack('basico')} data-id="element-280">
                  <div className="flex items-center justify-between p-4 md:p-5" data-id="element-281">
                    <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-282">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedPack === 'basico' ? 'border-yellow-400' : 'border-gray-500'}`} data-id="element-283">
                        {selectedPack === 'basico' && <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" data-id="element-284" />}
                      </div>
                      <div className="min-w-0" data-id="element-285">
                        <p className="font-bold text-white text-lg" data-id="element-286">
                          📦 PACK BÁSICO
                        </p>
                        <p className="text-gray-400 text-sm" data-id="element-287">
                          6 Tomos + 4 Bonos completos
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4" data-id="element-288">
                      <p className="text-gray-500 text-sm line-through" data-id="element-289">
                        $29.990
                      </p>
                      <p className="text-2xl md:text-3xl font-extrabold text-white" data-id="element-290">
                        $19.990
                      </p>
                    </div>
                  </div>
                  {selectedPack === 'basico' && <div className="px-5 pb-4 border-t border-gray-700 mx-4 mb-2" data-id="element-291">
                      <div className="pt-3 text-sm text-gray-300 space-y-1.5" data-id="element-292">
                        <p className="flex items-center gap-2" data-id="element-293">
                          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" data-id="element-294"></span>{' '}
                          Tomo 1 — Fundamentos de Nutrición y Salud Canina
                        </p>
                        <p className="flex items-center gap-2" data-id="element-295">
                          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" data-id="element-296"></span>{' '}
                          Tomo 2 — Panadería y Snacks Caninos
                        </p>
                        <p className="flex items-center gap-2" data-id="element-297">
                          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" data-id="element-298"></span>{' '}
                          Tomo 3 — Snacks Funcionales y Naturales
                        </p>
                        <p className="flex items-center gap-2" data-id="element-299">
                          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" data-id="element-300"></span>{' '}
                          Tomo 4 — Pasteles y Celebraciones Caninas
                        </p>
                        <p className="flex items-center gap-2" data-id="element-301">
                          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" data-id="element-302"></span>{' '}
                          Tomo 5 — Helados y Recetas Refrescantes
                        </p>
                        <p className="flex items-center gap-2" data-id="element-303">
                          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" data-id="element-304"></span>{' '}
                          Tomo 6 — Emprendimiento en Pastelería Canina
                        </p>
                        <p className="flex items-center gap-2" data-id="element-305">
                          <span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" data-id="element-306"></span>{' '}
                          4 Bonos exclusivos
                        </p>
                      </div>
                    </div>}
                </div>

                {/* Pack Completo - Highlighted */}
                <div className={`rounded-xl border-2 transition-all cursor-pointer relative ${selectedPack === 'completo' ? 'border-yellow-400 bg-gray-800 shadow-[0_0_20px_rgba(250,204,21,0.15)]' : 'border-yellow-400/60 bg-gray-800 hover:border-yellow-400'}`} onClick={() => setSelectedPack('completo')} data-id="element-307">
                  <div className="flex items-center justify-between p-4 md:p-5" data-id="element-308">
                    <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-309">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedPack === 'completo' ? 'border-yellow-400' : 'border-yellow-400/60'}`} data-id="element-310">
                        {selectedPack === 'completo' && <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" data-id="element-311" />}
                      </div>
                      <div className="min-w-0" data-id="element-312">
                        <p className="font-bold text-white text-lg" data-id="element-313">
                          ⭐ PACK COMPLETO
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1" data-id="element-314">
                          <span className="bg-yellow-400 text-gray-900 text-xs font-bold px-2 py-0.5 rounded" data-id="element-315">
                            MÁS ELEGIDO
                          </span>
                          <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded" data-id="element-316">
                            MEJOR VALOR
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm mt-1" data-id="element-317">
                          Pack Básico + 50 Recetas + 20 Clientes
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4" data-id="element-318">
                      <p className="text-gray-500 text-sm line-through" data-id="element-319">
                        $59.990
                      </p>
                      <p className="text-2xl md:text-3xl font-extrabold text-yellow-400" data-id="element-320">
                        $29.990
                      </p>
                    </div>
                  </div>
                  {selectedPack === 'completo' && <div className="px-5 pb-4 border-t border-gray-700 mx-4 mb-2" data-id="element-321">
                      <div className="pt-3 text-sm text-gray-300 space-y-1.5" data-id="element-322">
                        <p className="flex items-center gap-2" data-id="element-323">
                          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" data-id="element-324"></span>{' '}
                          Tomo 1 — Fundamentos de Nutrición y Salud Canina
                        </p>
                        <p className="flex items-center gap-2" data-id="element-325">
                          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" data-id="element-326"></span>{' '}
                          Tomo 2 — Panadería y Snacks Caninos
                        </p>
                        <p className="flex items-center gap-2" data-id="element-327">
                          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" data-id="element-328"></span>{' '}
                          Tomo 3 — Snacks Funcionales y Naturales
                        </p>
                        <p className="flex items-center gap-2" data-id="element-329">
                          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" data-id="element-330"></span>{' '}
                          Tomo 4 — Pasteles y Celebraciones Caninas
                        </p>
                        <p className="flex items-center gap-2" data-id="element-331">
                          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" data-id="element-332"></span>{' '}
                          Tomo 5 — Helados y Recetas Refrescantes
                        </p>
                        <p className="flex items-center gap-2" data-id="element-333">
                          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" data-id="element-334"></span>{' '}
                          Tomo 6 — Emprendimiento en Pastelería Canina
                        </p>
                        <p className="flex items-center gap-2" data-id="element-335">
                          <span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" data-id="element-336"></span>{' '}
                          4 Bonos exclusivos
                        </p>
                        <p className="flex items-center gap-2 mt-2 text-white font-semibold" data-id="element-337">
                          <span className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" data-id="element-338"></span>{' '}
                          + 50 Recetas Secretas de Alta Demanda
                        </p>
                        <p className="flex items-center gap-2 text-white font-semibold" data-id="element-339">
                          <span className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" data-id="element-340"></span>{' '}
                          + Tus Primeros 20 Clientes Vendiendo Snacks
                        </p>
                      </div>
                    </div>}
                </div>

                {/* Social proof note */}
                <p className="text-center text-gray-400 text-xs mt-3 italic" data-id="element-341">
                  🔥 El 80% de nuestros clientes elige el Pack Completo. Es el
                  que más valor tiene por su precio.
                </p>
              </div>

              {/* Buy Button */}
              <div className="mt-8 text-center" data-id="element-342">
                <Button variant="primary" size="xl" fullWidth onClick={handleBuyClick} className="" aria-label="Comprar el pack seleccionado" data-id="element-343">
                  👉 COMPRAR AHORA
                </Button>
                <div className="flex flex-col gap-1 text-sm text-gray-400 mt-4" data-id="element-344">
                  <span data-id="element-345">Descarga digital inmediata</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 9. Guarantee */}
        <section className="py-12 max-w-2xl mx-auto text-center cv-auto" data-id="element-346">
          <div className="border-4 border-dashed border-gray-300 rounded-xl p-8" data-id="element-347">
            <ShieldCheck className="w-16 h-16 text-green-500 mx-auto mb-4" aria-hidden="true" data-id="element-348" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-349">
              🔒 Garantía sin riesgo
            </h2>
            <p className="text-lg text-gray-700 mb-4" data-id="element-350">
              Probalo durante 7 días. Si no es para vos, te devolvemos el 100%
              del dinero.
            </p>
            <p className="font-bold text-gray-900" data-id="element-351">
              Sin preguntas. Sin letra chica.
            </p>
          </div>
        </section>

        {/* 10. FAQ */}
        <section className="py-16 max-w-3xl mx-auto cv-auto" data-id="element-352">
          <h2 className="text-3xl font-extrabold text-center mb-10" data-id="element-353">
            ❓ Preguntas frecuentes
          </h2>
          <Accordion items={[{
          title: '¿Es material digital?',
          content: 'Sí, se entrega en PDF y llega al instante a tu correo electrónico.'
        }, {
          title: '¿Necesito experiencia previa?',
          content: 'No, está diseñado para principiantes con instrucciones paso a paso.'
        }, {
          title: '¿Puedo vender lo que aprenda?',
          content: 'Sí, el Megapack incluye un módulo completo de emprendimiento para que inicies tu negocio.'
        }, {
          title: '¿Sirve si mi perro tiene alergias?',
          content: 'Sí, incluye adaptación de recetas para perros con alergias e intolerancias alimentarias.'
        }]} data-id="element-354" />
        </section>

        {/* 12. Final CTA */}
        <section className="py-16 text-center cv-auto" data-id="element-355">
          <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-356">
            🐶 La salud de tu perro no se improvisa
          </h2>
          <p className="text-xl text-gray-600 mb-8" data-id="element-357">
            Y tu emprendimiento tampoco.
          </p>

          <div className="bg-gray-100 inline-block px-6 py-3 rounded-lg mb-8" data-id="element-358">
            <p className="font-semibold text-gray-800" data-id="element-359">
              No necesitás más tiempo. Necesitás el sistema correcto.
            </p>
          </div>

          <div className="max-w-md mx-auto" data-id="element-360">
            <Button variant="primary" size="xl" fullWidth onClick={scrollToPlans} aria-label="Ver planes disponibles" data-id="element-361">
              👉 Accedé ahora al MEGAPACK
            </Button>
          </div>
        </section>

        {/* 13. Footer */}
        <footer className="py-8 text-center text-gray-500 text-sm border-t border-gray-200" data-id="element-362">
          <p className="mb-2" data-id="element-363">
            © 2026 DescargaTop – Megapack Pastelería y Nutrición Canina
          </p>
          <p data-id="element-364">
            Todos los derechos reservados | Privacidad · Términos y Condiciones
          </p>
        </footer>
      </main>
    </div>;
}